export type Appearance = "light" | "dark";
export type AppearancePreference = Appearance | "system";

export const APPEARANCE_COOKIE_NAME = "telia-webshop-appearance";
export const SYSTEM_APPEARANCE_COOKIE_NAME = "telia-webshop-system-appearance";
export const APPEARANCE_COOKIE_MAX_AGE_DAYS = 365;
export const DEFAULT_APPEARANCE: Appearance = "light";
export const DEFAULT_APPEARANCE_PREFERENCE: AppearancePreference = "system";
export const DARK_THEME_CLASS_NAME = "telia-dark";
export const LIGHT_THEME_CLASS_NAME = "telia-light";
export const THEME_ROOT_CLASS_NAME = "rapidshop-theme";
export const SYSTEM_APPEARANCE_WINDOW_KEY = "__SYSTEM_APPEARANCE__";

/** Parses a concrete appearance (also used for the system hint). Anything but "dark" is light. */
export const parseAppearance = (value: unknown): Appearance => (value === "dark" ? "dark" : DEFAULT_APPEARANCE);

/** Parses the stored user choice. No (or an invalid) value means "follow the system". */
export const parseAppearancePreference = (value: unknown): AppearancePreference =>
    value === "dark" || value === "light" ? value : DEFAULT_APPEARANCE_PREFERENCE;

/**
 * Dark mode is only supported on retailer pages; every other page stays light.
 * `appearance` is the user's preference. With "system" the OS-reported `systemAppearance` is used, falling back to light.
 */
export const resolveAppearance = ({
    isRetailer,
    appearance,
    systemAppearance,
    forceLight = false,
}: {
    isRetailer?: boolean;
    appearance?: unknown;
    systemAppearance?: unknown;
    forceLight?: boolean;
}): Appearance => {
    if (!isRetailer || forceLight) {
        return DEFAULT_APPEARANCE;
    }
    const preference = parseAppearancePreference(appearance);
    return preference === "system" ? parseAppearance(systemAppearance) : preference;
};
